import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '@/views/login.vue'
import Home from '@/views/dashboard'
import UsersList from '@/views/users-list'
import ClientsList from '@/views/clients-list'
import PageNotFound from '@/views/page-not-found'
import Orders from '@/views/orders'
import Calendar from '@/views/calendar'
import PermissionsDenied from '@/views/permissions-denied'
import Report from '@/views/report'
import DevPage from '@/views/dev-page'
import { tokenIsAvailable } from '@/helpers/tokenHandle'
import { getStorage } from '@/helpers/Local'
import WigCard from '@/views/[wig-id]-v2'
import WigPrintPage from '@/features/wig-page/components/wigs-details-print-page.vue'
import WigChanges from '@/views/wig-changes/[change-id].vue'
import WigCareHub from '@/views/wig-care-hub.vue'
import WigPayments from '@/views/payments/[wig-id].vue'
import WarehouseStock from '@/views/warehouse-stock.vue'
import Shipments from '@/views/shipments.vue'
import ShipmentCard from '@/views/[shipment-id].vue'
import CreateShipment from '@/views/create-shipment.vue'

Vue.use(VueRouter)

const getAllowed = (roles) => {
    const allowedRole = getStorage('access_role') || ''

    if (!allowedRole) return false

    return !!roles.filter(
        (role) =>
            role.toLowerCase() === allowedRole.toLowerCase() ||
            role.toLowerCase() === 'all'
    )[0]
}

const routes = [
    {
        path: '/',
        name: 'Home',
        meta: {
            layout: 'main',
        },
        component: Home,
    },
    {
        path: '/users-list',
        name: 'UsersList',
        meta: {
            layout: 'main',
        },
        component: UsersList,
    },
    {
        path: '/clients-list',
        name: 'ClientsList',
        meta: {
            layout: 'main',
        },
        component: ClientsList,
    },
    {
        path: '/orders',
        name: 'Orders',
        meta: {
            layout: 'main',
        },
        component: Orders,
    },
    {
        path: '/orders/:wigID',
        name: 'wigCard',
        meta: {
            layout: 'main',
        },
        component: WigCard,
    },
    {
        path: '/orders/print/:wigID',
        name: 'wigPrintPage',
        component: WigPrintPage,
    },
    {
        path: '/meetings-calendar',
        name: 'Calendar',
        meta: {
            layout: 'main',
        },
        component: Calendar,
    },
    {
        path: '/login',
        name: 'Login',
        component: Login,
    },
    {
        path: '/permission-denied',
        name: 'PermissionDenied',
        meta: {
            layout: 'main',
        },
        component: PermissionsDenied,
    },
    {
        path: '*',
        name: 'PageNotFound',
        component: PageNotFound,
    },
    {
        path: '/report',
        name: 'Report',
        meta: {
            layout: 'main',
        },
        component: Report,
    },
    {
        path: '/wig-care-hub',
        name: 'WigCareHub',
        meta: {
            layout: 'main',
        },
        component: WigCareHub,
    },
    {
        path: '/dev-page',
        name: 'DevPage',
        meta: {
            layout: 'main',
        },
        component: DevPage,
    },
    {
        path: '/wig-changes/:changeID',
        name: 'wigChanges',
        meta: {
            layout: 'empty-protected',
        },
        component: WigChanges,
    },
    {
        path: '/orders/add-payment/:wigID',
        name: 'wigPaymentPage',
        meta: {
            layout: 'main',
        },
        component: WigPayments,
    },
    {
        path: '/warehouse-stock',
        name: 'WarehouseStock',
        meta: {
            layout: 'main',
        },
        component: WarehouseStock,
    },
    {
        path: '/shipments',
        name: 'Shipments',
        meta: {
            layout: 'main',
        },
        component: Shipments,
    },
    {
        path: '/shipments/:id',
        name: 'ShipmentCard',
        meta: {
            layout: 'main',
        },
        component: ShipmentCard,
    },
    {
        path: '/shipments-create',
        name: 'CreateShipment',
        meta: {
            layout: 'main',
        },
        component: CreateShipment,
    },
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
})

router.beforeEach((to, from, next) => {
    const token = tokenIsAvailable()
    const storedRole = getStorage('access_role') || ''

    const cases = [
        {
            case: 'unauthorized',
            condition: () => to.name !== 'Login' && !token,
        },
        {
            case: 'authorized',
            condition: () => to.name === 'Login' && token,
        },
        {
            case: 'noneRole',
            condition: () =>
                token &&
                to.name !== 'Login' &&
                (!storedRole || storedRole.toLowerCase() === 'none'),
        },
        {
            case: 'worker',
            condition: () =>
                token &&
                getAllowed(['worker']) &&
                ['UsersList', 'Calendar', 'ClientsList'].includes(to.name),
        },
        {
            case: 'shipmentAccessDenied',
            condition: () =>
                token &&
                !getAllowed(['admin', 'manager']) &&
                ['Shipments', 'ShipmentCard', 'CreateShipment'].includes(
                    to.name
                ),
        },
    ]

    const action = cases.reduce((ak, item) => {
        return item.condition() ? (ak = item.case) : ak
    }, '')

    switch (action) {
        case 'unauthorized':
            next({ name: 'Login' })
            return
        case 'authorized':
            next({ path: '/' })
            return
        case 'noneRole':
            next({ name: 'Login' })
            return
        case 'worker':
            next({ name: 'PermissionDenied' })
            return
        case 'shipmentAccessDenied':
            next({ name: 'PermissionDenied' })
            return
        default:
            next()
            return
    }
})

export default router
